import * as React from 'react';

// For a couple of fields the Rich Text field type seemed to be the most practical, as
// it
// 1. allows the text to be reasonably long (unlike Short Text)
// 2. allows control over the formatting options (unlike Long Text).
// 3. allows us to change our minds about the formatting options withouth losing
//    Contentful data.
// There are tools online to interpret Contentful rich text content in full complexity
// but for this project the intention is to keep it very simple (don't let the content editors
// do a lot of formatting). So we have a (relatively) simple component here to pick up one or
// more lines of unformatted text.

export interface RichTextObject {
  json: object;
}

interface UriData {
  uri?: string;
}

type Mark = {
  [key: string]: string | undefined;
};

interface Node {
  nodeType: string;
  value: string;
  marks: Array<Mark>;
  data: UriData;
  content: Array<Node>;
}

type PropsType = {
  richText?: RichTextObject;
  component?: React.LegacyFunctionComponentWithChildren;
};

// recursion to follow the json structure
const showNode = (
  node: Node,
  parent: Node | null,
  uniqueKey: string,
  component?: React.LegacyFunctionComponentWithChildren
): JSX.Element | null => {
  // helper to process the nodes marked as bold (or later possibly more markups)
  const isContentElementMarkedAs = (contentElement: Node, markKey: string, markValue: string) => {
    if (!Array.isArray(contentElement.marks)) {
      return false;
    }
    for (const mark of contentElement.marks) {
      if (mark[markKey] === markValue) {
        return true;
      }
    }
    return false;
  };

  switch (node.nodeType) {
    case 'text':
      if (!node.value) {
        return null;
      }
      if (isContentElementMarkedAs(node, 'type', 'bold')) {
        return <b key={uniqueKey}>{node.value}</b>;
      }
      return <React.Fragment key={uniqueKey}>{node.value}</React.Fragment>;
    case 'paragraph': {
      if (!Array.isArray(node.content)) {
        return null;
      }
      const singleLine = parent?.content?.length === 1;
      const lineContent = (
        <React.Fragment key={uniqueKey}>
          {node.content.map((contentElement, index) =>
            showNode(contentElement, node, uniqueKey + '-' + index, component)
          )}
        </React.Fragment>
      );
      if (component) {
        // when we need to display the paragraph differently e.g. in the FAQ
        const C = component;
        return <C key={uniqueKey}>{lineContent}</C>;
      }
      // if there is only a single piece of text in the content don't add markup
      return singleLine ? lineContent : <p key={uniqueKey}>{lineContent}</p>;
    }
    case 'document':
      if (!Array.isArray(node.content)) {
        return null;
      }
      return (
        <React.Fragment key={uniqueKey}>
          {node.content.map((contentElement, index: number) =>
            showNode(contentElement, node, uniqueKey + '-' + index, component)
          )}
        </React.Fragment>
      );
    case 'hyperlink': {
      const uri = node.data.uri;
      if (typeof uri === 'string') {
        const uriPieces = uri.split(' ');
        type SimpleHref = { href: string; target?: string };
        const linkProps: SimpleHref = { href: uriPieces[0] };
        if (uriPieces.length > 1) {
          linkProps.target = '_BLANK';
        }
        const lineContent: JSX.Element = (
          <React.Fragment key={uniqueKey}>
            {node.content.map((contentElement, index: number) =>
              showNode(contentElement, node, `${uniqueKey}-${index}`, component)
            )}
          </React.Fragment>
        );
        return (
          <a {...linkProps} key={uniqueKey}>
            {lineContent}
          </a>
        );
      }
      return null;
    }
    default:
      return null;
  }
};

const SimpleRichTextContentful = (props: PropsType): JSX.Element | null => {
  const { richText, component } = props;
  if (!richText) {
    return null;
  }
  return showNode(richText.json as Node, null, '1', component);
};

export default SimpleRichTextContentful;
